import { useState, useRef, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Send, Bot, User, Loader2, Paperclip, CheckCircle2, XCircle, Zap, ToggleLeft, ToggleRight } from 'lucide-react'
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query'
import api from '../services/api'
import { useUser } from '../hooks/useUser'
import { useLanguage } from '../hooks/useLanguage'
import TourGuide, { TourStep } from '../components/TourGuide'
// PongoFin logo - enterprise blue sphere
import VoiceTranscriber from '../components/VoiceTranscriber'
import AdrAgentPanel from '../components/AdrAgentPanel'
import { useAdrAgent } from '../hooks/useAdrAgent'

// Action type from AI response
interface ChatAction {
  type: 'categorize_all' | 'categorize_similar' | 'apply_rule' | 'bulk_categorize'
  params?: {
    pattern?: string
    category_id?: string
    transaction_ids?: string[]
  }
  description: string
}

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  contextUsed?: number
  transactionsLoaded?: number
  isTyping?: boolean
  user_id?: number | string
  user_name?: string
}

interface ChatConversation {
  id: string
  title: string
  created_at: string
  updated_at: string
  message_count: number
}

interface UploadStatus {
  filename: string
  status: 'uploading' | 'success' | 'error'
  message?: string
  transactionCount?: number
}

// Typewriter hook
function useTypewriter(text: string, speed: number = 15, enabled: boolean = true) {
  const [displayedText, setDisplayedText] = useState('')
  const [isComplete, setIsComplete] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setDisplayedText(text)
      setIsComplete(true)
      return
    }

    setDisplayedText('')
    setIsComplete(false)
    
    if (!text) return

    let currentIndex = 0
    const interval = setInterval(() => {
      if (currentIndex < text.length) {
        // Add multiple characters at once for faster typing
        const charsToAdd = Math.min(3, text.length - currentIndex)
        setDisplayedText(text.slice(0, currentIndex + charsToAdd))
        currentIndex += charsToAdd
      } else {
        setIsComplete(true)
        clearInterval(interval)
      }
    }, speed)

    return () => clearInterval(interval)
  }, [text, speed, enabled])

  return { displayedText, isComplete }
}

// Typewriter message component
function TypewriterMessage({ 
  content, 
  isNew, 
  onComplete,
  onProgress,
  formatMarkdown 
}: { 
  content: string
  isNew: boolean
  onComplete: () => void
  onProgress?: () => void
  formatMarkdown: (text: string) => string
}) {
  const { displayedText, isComplete } = useTypewriter(content, 12, isNew)

  useEffect(() => {
    if (isComplete && isNew) {
      onComplete()
    }
  }, [isComplete, isNew, onComplete])

  useEffect(() => {
    if (isNew && onProgress) {
      onProgress()
    }
  }, [displayedText, isNew, onProgress])

  return (
    <div 
      className="whitespace-pre-wrap prose prose-sm max-w-none" 
      dangerouslySetInnerHTML={{ __html: formatMarkdown(displayedText) }} 
    />
  )
}

export default function Chat() {
  const { user } = useUser()
  const { t, language } = useLanguage()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<UploadStatus | null>(null)
  const [typingMessageId, setTypingMessageId] = useState<string | null>(null)
  const [localPendingChatId, setLocalPendingChatId] = useState<string | null>(null)
  const [executingAction, setExecutingAction] = useState<string | null>(null)
  const [executedActions, setExecutedActions] = useState<Set<string>>(new Set())
  const [agentMode, setAgentMode] = useState(false)
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const lastLoadedChatIdRef = useRef<string | null>(null)

  const isIt = language === 'it'

  // ADR Agent hook
  const adrAgent = useAdrAgent()

  const currentChatId = searchParams.get('c')

  // Fetch chats list
  const { data: chatsData, refetch: refetchChats } = useQuery({
    queryKey: ['chats'],
    queryFn: async () => {
      const response = await api.get('/chats')
      return response.data.chats as ChatConversation[]
    }
  })

  const chats = chatsData || []

  // Create new chat mutation
  const createChatMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/chats', { title: 'Nuova conversazione' })
      return response.data.chat
    },
    onSuccess: () => {
      refetchChats()
    }
  })

  // Execute chat action mutation
  const executeActionMutation = useMutation({
    mutationFn: async (action: ChatAction) => {
      const response = await api.post('/chat/actions', {
        action: action.type,
        params: action.params
      })
      return response.data
    },
    onSuccess: (data, action) => {
      // Add result message to chat
      const resultMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `✅ **Azione completata!**\n\n${data.message}\n\n${data.details && data.details.length > 0 ? `Dettagli:\n${data.details.slice(0, 10).map((d: { description: string; category: string }) => `- ${d.description} → ${d.category}`).join('\n')}${data.details.length > 10 ? `\n... e altri ${data.details.length - 10}` : ''}` : ''}`,
        timestamp: new Date().toISOString(),
        isTyping: true
      }
      setMessages(prev => [...prev, resultMessage])
      setTypingMessageId(resultMessage.id)
      
      // Save to chat
      if (currentChatId) {
        saveMessages(currentChatId, [...messages, resultMessage])
      }
      
      // Invalidate transactions to refresh data
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['uncategorized-transactions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      
      // Mark action as executed
      setExecutedActions(prev => new Set([...prev, JSON.stringify(action)]))
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const errorMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `❌ **Errore nell'esecuzione dell'azione:**\n\n${error.response?.data?.message || error.message || 'Errore sconosciuto'}`,

        timestamp: new Date().toISOString(),
        isTyping: true
      }
      setMessages(prev => [...prev, errorMessage])
      setTypingMessageId(errorMessage.id)
    },
    onSettled: () => {
      setExecutingAction(null)
    }
  })

  // Parse action from message content
  const parseAction = useCallback((content: string): ChatAction | null => {
    const actionMatch = content.match(/```action\s*\n?([\s\S]*?)\n?```/)
    if (actionMatch) {
      try {
        return JSON.parse(actionMatch[1]) as ChatAction
      } catch {
        return null
      }
    }
    return null
  }, [])

  // Remove action block from displayed content
  const removeActionBlock = useCallback((content: string): string => {
    return content.replace(/```action\s*\n?[\s\S]*?\n?```/g, '').trim()
  }, [])

  const loadChat = useCallback(async (chatId: string) => {
    try {
      const response = await api.get(`/chats/${chatId}`)
      const chat = response.data.chat
      setMessages(chat.messages.map((m: Message) => ({
        ...m,
        timestamp: m.timestamp,
        isTyping: false
      })))
      setTypingMessageId(null)
    } catch (error) {
      console.error('Failed to load chat:', error)
      setSearchParams({})
    }
  }, [setSearchParams])

  // Load chat when ID changes
  useEffect(() => {
    if (currentChatId) {
      // Do not reload from backend while local state is still the source of truth for a just-created chat.
      if (localPendingChatId === currentChatId) {
        return
      }

      // Avoid reloading the same chat on each local state change (prevents message flicker/disappear).
      if (lastLoadedChatIdRef.current === currentChatId) {
        return
      }

      lastLoadedChatIdRef.current = currentChatId
      loadChat(currentChatId)
    } else {
      setMessages([])
      setTypingMessageId(null)
      lastLoadedChatIdRef.current = null
    }
  }, [currentChatId, loadChat, localPendingChatId])

  const saveMessages = useCallback(async (chatId: string, msgs: Message[]) => {
    try {
      // Remove isTyping flag before saving
      const cleanMsgs = msgs.map(({ isTyping: _isTyping, ...rest }) => rest)
      await api.put(`/chats/${chatId}`, { messages: cleanMsgs })
      refetchChats()
    } catch (error) {
      console.error('Failed to save messages:', error)
    }
  }, [refetchChats])

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Simple markdown to HTML converter with better table support
  const formatMarkdown = useCallback((text: string): string => {
    // First, handle tables separately
    const tableRegex = /(\|.+\|\n)+/g
    let html = text
    
    // Process tables
    html = html.replace(tableRegex, (tableMatch) => {
      const lines = tableMatch.trim().split('\n').filter(line => line.trim())
      if (lines.length === 0) return tableMatch
      
      let tableHtml = '<div class="overflow-x-auto my-3"><table class="min-w-full border-collapse text-sm">'
      let isFirstDataRow = true
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const cells = line.split('|').filter(c => c.trim() !== '').map(c => c.trim())
        
        // Skip separator line (contains ---)
        if (cells.some(c => /^[-:]+$/.test(c))) {
          continue
        }
        
        // First non-separator row is header
        if (isFirstDataRow) {
          tableHtml += '<thead class="bg-gray-100"><tr>'
          cells.forEach(cell => {
            tableHtml += `<th class="border border-gray-300 px-2 py-1.5 text-left font-semibold text-gray-700 whitespace-nowrap text-[10px]">${cell}</th>`
          })
          tableHtml += '</tr></thead><tbody>'
          isFirstDataRow = false
        } else {
          tableHtml += '<tr class="hover:bg-gray-50">'
          cells.forEach(cell => {
            tableHtml += `<td class="border border-gray-300 px-2 py-1.5 whitespace-nowrap text-[10px]">${cell}</td>`
          })
          tableHtml += '</tr>'
        }
      }
      
      tableHtml += '</tbody></table></div>'
      return tableHtml
    })
    
    // Then process other markdown
    html = html
      // Headers
      .replace(/^### (.+)$/gm, '<h3 class="font-semibold text-xs mt-2 mb-1">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="font-bold text-xs mt-3 mb-1.5">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="font-bold text-sm mt-3 mb-1.5">$1</h1>')
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Italic (but not in already processed content)
      .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
      // Code blocks
      .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-xs">$1</code>')
      // Lists
      .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
      .replace(/^• (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
      .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>')
      // Reduce multiple newlines to max 2 (one paragraph break)
      .replace(/\n{3,}/g, '\n\n')
      // Convert double newlines to paragraph breaks
      .replace(/\n\n/g, '</p><p class="mt-2">')
      // Convert remaining single newlines to <br/> only if not before HTML tags
      .replace(/\n(?!<)/g, '<br/>')
    
    // Wrap in paragraph if we added paragraph breaks
    if (html.includes('</p><p')) {
      html = '<p>' + html + '</p>'
    }
    
    return html
  }, [])

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return

    // Create chat if none selected
    let chatId = currentChatId
    if (!chatId) {
      const newChat = await createChatMutation.mutateAsync()
      chatId = newChat.id
      if (chatId) {
        setLocalPendingChatId(chatId)
        setSearchParams({ c: chatId })
      }
    }

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString(),
      user_id: user?.id,
      user_name: user?.name || user?.email?.split('@')[0],
    }

    const pendingAssistantId = crypto.randomUUID()
    const pendingAssistantMessage: Message = {
      id: pendingAssistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isTyping: true,
    }

    const newMessages = [...messages, userMessage, pendingAssistantMessage]
    setMessages(newMessages)
    setTypingMessageId(pendingAssistantId)
    setInput('')
    setIsLoading(true)

    try {
      const conversationHistory = [...messages, userMessage].map(m => ({
        role: m.role,
        content: m.content,
      }))

      const response = await api.post('/chat', {
        message: userMessage.content,
        conversation_history: conversationHistory,
      }, {
        timeout: 120000,
      })

      const finalMessages = newMessages.map((msg) =>
        msg.id === pendingAssistantId
          ? {
              ...msg,
              content: response.data.response,
              contextUsed: response.data.context_vectors,
              transactionsLoaded: response.data.transactions_loaded,
              isTyping: true,
            }
          : msg
      )
      setMessages(finalMessages)
      
      // Save to backend (will be saved after typing completes)
      if (chatId) {
        saveMessages(chatId, finalMessages)
      }
    } catch (error) {
      console.error('Chat error:', error)
      const finalMessages = newMessages.map((msg) =>
        msg.id === pendingAssistantId
          ? {
              ...msg,
              content: 'Mi dispiace, si è verificato un errore. Riprova più tardi.',
              isTyping: true,
            }
          : msg
      )
      setMessages(finalMessages)
      if (chatId) {
        saveMessages(chatId, finalMessages)
      }
    } finally {
      setIsLoading(false)
      setLocalPendingChatId(null)
    }
  }

  const handleTypingComplete = useCallback((messageId: string) => {
    setTypingMessageId((prev) => (prev === messageId ? null : prev))
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, isTyping: false } : m
    ))
  }, [])

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadStatus({ filename: file.name, status: 'uploading' })

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post('/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      setUploadStatus({
        filename: file.name,
        status: 'success',
        message: 'File caricato con successo!',
        transactionCount: response.data.row_count,
      })

      // Invalida cache uploads
      queryClient.invalidateQueries({ queryKey: ['uploads'] })

      // Aggiungi messaggio nella chat
      const systemMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `✅ **File caricato:** ${file.name}\n\n📊 ${response.data.row_count} transazioni trovate e pronte per l'analisi.\n\nOra puoi chiedermi qualsiasi cosa sui tuoi movimenti!`,
        timestamp: new Date().toISOString(),
        isTyping: true,
      }
      const newMessages = [...messages, systemMessage]
      setMessages(newMessages)
      setTypingMessageId(systemMessage.id)
      
      if (currentChatId) {
        saveMessages(currentChatId, newMessages)
      }

      // Reset dopo 3 secondi
      setTimeout(() => setUploadStatus(null), 3000)

    } catch (error: unknown) {
      const err = error as { response?: { data?: { duplicate_filename?: string; message?: string }; status?: number } }
      const errorData = err.response?.data
      const isDuplicate = err.response?.status === 409

      setUploadStatus({
        filename: file.name,
        status: 'error',
        message: isDuplicate 
          ? `File già caricato: ${errorData?.duplicate_filename || 'duplicato'}`
          : errorData?.message || 'Errore durante il caricamento',
      })

      // Reset dopo 5 secondi
      setTimeout(() => setUploadStatus(null), 5000)
    }

    // Reset input file
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // Voice transcript handler — inserts text into input, does NOT send
  const handleVoiceTranscript = useCallback((text: string) => {
    setInput(prev => prev ? `${prev} ${text}` : text)
  }, [])

  // Agent mode toggle
  const toggleAgentMode = useCallback(() => {
    setAgentMode(prev => {
      const next = !prev
      if (next) {
        // Entering agent mode — reset ADR state
        adrAgent.reset()
      }
      return next
    })
  }, [adrAgent])

  // Tour steps for Chat
  const tourSteps: TourStep[] = language === 'it' ? [
    {
      title: 'Benvenuto nell\'Assistente AI! 🤖',
      content: 'Questa chat è alimentata da intelligenza artificiale. Puoi chiedere analisi, categorizzazione e informazioni sulle tue transazioni.',
      position: 'center'
    },
    {
      target: '[data-tour="chat-input"]',
      title: 'Invia Messaggi',
      content: 'Scrivi qui le tue domande. Esempi: "Quali sono le mie spese principali?" o "Categorizza le transazioni Spotify".',
      position: 'top'
    },
    {
      title: 'Storico Chat nel Menu',
      content: 'Trovi lo storico conversazioni direttamente nel menu di sinistra, sotto la voce Chat.',
      position: 'center'
    }
  ] : [
    {
      title: 'Welcome to AI Assistant! 🤖',
      content: 'This chat is powered by artificial intelligence. You can ask for analysis, categorization and information about your transactions.',
      position: 'center'
    },
    {
      target: '[data-tour="chat-input"]',
      title: 'Send Messages',
      content: 'Type your questions here. Examples: "What are my main expenses?" or "Categorize Spotify transactions".',
      position: 'top'
    },
    {
      title: 'History In Left Menu',
      content: 'You can find chat history directly in the left menu, under the Chat item.',
      position: 'center'
    }
  ]

  return (
    <>
      <TourGuide tourId="chat" steps={tourSteps} />
      <div className="space-y-4 pt-4">
        {/* Header - Same style as other pages */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              {/* Page title now in top bar */}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {currentChatId ? (chats.find(c => c.id === currentChatId)?.title || t.chat.conversation) : t.chat.newConversation}
            </p>
          </div>
          {/* Agent Mode Toggle */}
          <button
            onClick={toggleAgentMode}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              agentMode
                ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border border-teal-300 dark:border-teal-700'
                : 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-slate-700 hover:bg-gray-200 dark:hover:bg-slate-700'
            }`}
            title={isIt ? 'Modalità Agent ADR/SID' : 'Agent Mode ADR/SID'}
          >
            {agentMode
              ? <ToggleRight className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              : <ToggleLeft className="w-5 h-5" />
            }
            <span className="hidden sm:inline">{isIt ? 'Agent' : 'Agent'}</span>
            <span className={`w-2 h-2 rounded-full ${agentMode ? 'bg-teal-500 animate-pulse' : 'bg-gray-300 dark:bg-gray-600'}`} />
          </button>
        </div>
        
        {/* Chat Container */}
        <div className="flex h-[calc(100vh-12rem)] -mx-4 lg:-mx-8 relative overflow-hidden">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 order-1">
        {/* Agent Mode Panel OR Regular Messages */}
        {agentMode ? (
          <AdrAgentPanel
            state={adrAgent.state}
            currentField={adrAgent.currentField}
            currentOptions={adrAgent.currentOptions}
            values={adrAgent.values}
            errors={adrAgent.errors}
            warnings={adrAgent.warnings}
            messages={adrAgent.messages}
            isLoading={adrAgent.isLoading}
            personaType={adrAgent.personaType}
            generatedContent={adrAgent.generatedContent}
            generatedFilename={adrAgent.generatedFilename}
            schema={adrAgent.schema}
            isIt={isIt}
            rapporti={adrAgent.rapporti}
            rapportoTypeOptions={adrAgent.rapportoTypeOptions}
            ruoloOptions={adrAgent.ruoloOptions}
            onStart={adrAgent.startWorkflow}
            onSetField={adrAgent.setField}
            onGoBack={adrAgent.goBack}
            onGoToReview={adrAgent.goToReview}
            onGoBackFromRapporti={adrAgent.goBackFromRapporti}
            onValidate={adrAgent.validate}
            onGenerate={adrAgent.generate}
            onSaveDraft={adrAgent.saveDraft}
            onReset={adrAgent.reset}
            onRetryFromError={adrAgent.retryFromError}
            onUpdateRapporto={adrAgent.updateRapporto}
            onAddRapporto={adrAgent.addRapporto}
            onRemoveRapporto={adrAgent.removeRapporto}
            onUpdateAnagrafica={adrAgent.updateAnagrafica}
            onAddAnagrafica={adrAgent.addAnagrafica}
            onRemoveAnagrafica={adrAgent.removeAnagrafica}
          />
        ) : (
        <>
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-slate-900 p-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Bot className="w-10 h-10 mb-3" />
              <p className="text-sm font-medium">{t.chat.startConversation}</p>
              <p className="text-xs mt-2 text-center max-w-md">
                {t.chat.helpText}
              </p>
              <div className="mt-4 space-y-2 text-xs">
                <p className="font-medium text-gray-600">{t.chat.examples}</p>
                <ul className="space-y-0.5 text-gray-500">
                  <li>• {t.chat.example1}</li>
                  <li>• {t.chat.example2}</li>
                  <li>• {t.chat.example3}</li>
                  <li>• {t.chat.example4}</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message, idx) => {
                const isMostRecentUserMessage =
                  message.role === 'user' &&
                  !messages.slice(idx + 1).some((m) => m.role === 'user')

                return (
                <div
                  key={message.id}
                  className={`flex gap-3 transition-all duration-200 ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                      <div className="logo-sphere" style={{ width: 20, height: 20 }} />
                    </div>
                  )}
                  <div className={`max-w-[70%] ${message.role === 'user' ? 'text-right' : ''}`}>
                    {message.role === 'user' && message.user_name && (
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 mr-1 font-medium">
                        {message.user_name}
                      </div>
                    )}
                    {message.role === 'assistant' && (
                      <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-0.5 ml-1 font-medium flex items-center gap-1">
                        Assistente
                        <span className="inline-flex items-center px-1 py-0 rounded text-[8px] font-bold bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 leading-tight">AI</span>
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-3 py-2 text-xs ${
                        message.role === 'user'
                          ? `bg-blue-600 text-white ${isMostRecentUserMessage ? 'animate-pulse' : ''}`
                          : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-800 dark:text-gray-200'
                      }`}
                    >
                    {message.role === 'assistant' && typingMessageId === message.id && !message.content.trim() ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-blue-600 dark:text-blue-300">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm font-medium">
                            {language === 'it'
                              ? 'Sto analizzando i dati e preparando la risposta...'
                              : 'Analyzing data and preparing response...'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500/70 animate-pulse" />
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500/70 animate-pulse [animation-delay:120ms]" />
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500/70 animate-pulse [animation-delay:240ms]" />
                        </div>
                      </div>
                    ) : message.role === 'assistant' && typingMessageId === message.id ? (
                      <TypewriterMessage
                        content={removeActionBlock(message.content)}
                        isNew={true}
                        onComplete={() => handleTypingComplete(message.id)}
                        onProgress={() => scrollToBottom('auto')}
                        formatMarkdown={formatMarkdown}
                      />
                    ) : message.role === 'user' ? (
                      <div className="whitespace-pre-wrap">{message.content}</div>
                    ) : (
                      <div className="whitespace-pre-wrap text-xs prose prose-xs max-w-none" 
                        dangerouslySetInnerHTML={{ __html: formatMarkdown(removeActionBlock(message.content)) }} 
                      />
                    )}
                    {/* Action Button */}
                    {message.role === 'assistant' && parseAction(message.content) && (
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                        {(() => {
                          const action = parseAction(message.content)!
                          const actionKey = JSON.stringify(action)
                          const isExecuted = executedActions.has(actionKey)
                          const isExecuting = executingAction === actionKey
                          
                          return (
                            <button
                              onClick={() => {
                                setExecutingAction(actionKey)
                                executeActionMutation.mutate(action)
                              }}
                              disabled={isExecuted || isExecuting}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                isExecuted 
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 cursor-default'
                                  : isExecuting
                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 cursor-wait'
                                    : 'bg-primary-600 hover:bg-primary-700 text-white'
                              }`}
                            >
                              {isExecuting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : isExecuted ? (
                                <CheckCircle2 className="w-4 h-4" />
                              ) : (
                                <Zap className="w-4 h-4" />
                              )}
                              {isExecuted ? 'Azione completata' : isExecuting ? 'Esecuzione...' : action.description}
                            </button>
                          )
                        })()}
                      </div>
                    )}
                    <div
                      className={`text-xs mt-2 ${
                        message.role === 'user' ? 'text-blue-200' : 'text-gray-400'
                      }`}
                    >
                      {new Date(message.timestamp).toLocaleTimeString('it-IT', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {message.transactionsLoaded !== undefined && message.transactionsLoaded > 0 && (
                        <span className="ml-2">
                          • {message.transactionsLoaded} transazioni analizzate
                        </span>
                      )}
                    </div>
                    </div>
                  </div>
                  {message.role === 'user' && (
                    <div className="flex-shrink-0 w-6 h-6 bg-gray-200 dark:bg-slate-700 rounded-full flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
                    </div>
                  )}
                </div>
              )})}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
        </>
        )}

        {/* Upload Status Banner */}
        {uploadStatus && (
          <div className={`mx-4 mb-3 px-4 py-3 rounded-xl flex items-center gap-3 ${
            uploadStatus.status === 'uploading' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
            uploadStatus.status === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
            'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          }`}>
            {uploadStatus.status === 'uploading' && <Loader2 className="w-5 h-5 animate-spin" />}
            {uploadStatus.status === 'success' && <CheckCircle2 className="w-5 h-5" />}
            {uploadStatus.status === 'error' && <XCircle className="w-5 h-5" />}
            <div className="flex-1">
              <span className="font-medium">{uploadStatus.filename}</span>
              {uploadStatus.message && <span className="ml-2">• {uploadStatus.message}</span>}
            </div>
            {uploadStatus.transactionCount && (
              <span className="text-sm">{uploadStatus.transactionCount} transazioni</span>
            )}
          </div>
        )}

        {/* Input Area — Apple-style capsule */}
        <div data-tour="chat-input" className="flex-shrink-0 px-4 pb-4 pt-2">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-end gap-2 bg-white dark:bg-slate-800 rounded-2xl border border-gray-200/80 dark:border-slate-600/60 shadow-sm hover:shadow-md transition-shadow duration-200 px-3 py-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".csv,.xlsx,.xls,.pdf"
                className="hidden"
              />
              {/* Left action buttons */}
              <div className="flex items-center gap-0.5 pb-0.5">
                {!agentMode && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading || uploadStatus?.status === 'uploading'}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title={language === 'it' ? 'Carica estratto conto' : 'Upload bank statement'}
                  >
                    <Paperclip className="w-[18px] h-[18px]" />
                  </button>
                )}
                <VoiceTranscriber
                  onTranscript={handleVoiceTranscript}
                  disabled={isLoading}
                  className="[&_button]:!border-0 [&_button]:!rounded-full [&_button]:!px-2 [&_button]:!py-2 [&_button]:!shadow-none [&_button]:hover:!bg-gray-100 [&_button]:dark:hover:!bg-slate-700"
                />
              </div>
              {/* Textarea */}
              <textarea
                value={input}
                onChange={(e) => {
                  setInput(e.target.value)
                  // Auto-resize
                  e.target.style.height = 'auto'
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'
                }}
                onKeyPress={handleKeyPress}
                placeholder={agentMode
                  ? (isIt ? 'Parla con l\'agente...' : 'Talk to the agent...')
                  : t.chat.placeholder
                }
                rows={1}
                className="flex-1 resize-none bg-transparent px-1 py-1.5 text-sm leading-relaxed focus:outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 min-h-[36px] max-h-[120px]"
                disabled={isLoading}
                style={{ height: 'auto' }}
              />
              {/* Send button */}
              <button
                onClick={() => {
                  sendMessage()
                  // Reset textarea height
                  const ta = document.querySelector('[data-tour="chat-input"] textarea') as HTMLTextAreaElement
                  if (ta) ta.style.height = 'auto'
                }}
                disabled={!input.trim() || isLoading}
                className={`p-2 rounded-full transition-all duration-200 mb-0.5 flex-shrink-0 ${
                  input.trim() && !isLoading
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow active:scale-95'
                    : 'bg-gray-100 dark:bg-slate-700 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                }`}
              >
                <Send className="w-[18px] h-[18px]" />
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
    </div>
    </>
  )
}
